import React from 'react';
import Button from '../common/Button';

const ServiceList = ({ services, clinicServices = [], onAddService, onSelectService, loading }) => {
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Check if a service is already added to the clinic
    const isServiceAdded = (serviceId) => {
        return clinicServices.some(clinicService => clinicService.serviceId === serviceId);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading services...</p>
                </div>
            </div>
        );
    }

    if (!services || services.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <p className="text-gray-500">No services available.</p>
                <p className="text-gray-400 text-sm">Click "Add New Service" to get started.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Available Services</h3>
                    <p className="text-sm text-gray-500">
                        {services.length} service{services.length !== 1 ? 's' : ''} available
                    </p>
                </div>
                <Button
                    type="button"
                    variant="primary"
                    onClick={onAddService}
                    className="w-full sm:w-auto"
                >
                    Add New Service
                </Button>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {services.map((service) => {
                    const isAdded = isServiceAdded(service.id);
                    
                    return (
                        <div key={service.id} className={`bg-white rounded-lg shadow-sm border transition-shadow ${
                            isAdded ? 'opacity-60' : 'hover:shadow-md'
                        }`}>
                            <div className="p-4 sm:p-6">
                                {/* Service Header */}
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0 mb-4">
                                    <div className="flex-1">
                                        <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-1">
                                            {service.name}
                                        </h4>
                                        <p className="text-sm text-gray-500">
                                            Code: {service.shortCode}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                        {service.isMultiSession && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                Multi-Session
                                            </span>
                                        )}
                                        {service.category && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {service.category}
                                            </span>
                                        )}
                                        {isAdded && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                Added
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Service Description */}
                                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                                    {service.description}
                                </p>

                                {/* Service Details */}
                                <div className="space-y-2 mb-4">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Type:</span>
                                        <span className="text-gray-900">
                                            {service.isMultiSession ? 'Multi-Session' : 'Single Session'}
                                        </span>
                                    </div>
                                    {service.category && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Category:</span>
                                            <span className="text-gray-900">{service.category}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Created:</span>
                                        <span className="text-gray-900">{formatDate(service.createdAt)}</span>
                                    </div>
                                </div>

                                {/* Action Button */}
                                <Button
                                    type="button"
                                    variant={isAdded ? "secondary" : "primary"}
                                    size="sm"
                                    onClick={() => onSelectService(service)}
                                    className="w-full"
                                    disabled={isAdded}
                                >
                                    {isAdded ? 'Already Added' : 'Add to Clinic'}
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ServiceList; 